/**
 * Cria Slot + Appointment de Pronto Atendimento.
 * createAppointment = só marca; startEncounter = inicia Encounter (verifyEncounter).
 * Remoto/chat: cria sala Whereby (mesmo fluxo da agenda normal) antes do PostAppointment.
 */
import { useState } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { endpoints, oids } from "@/configs/Settings";
import { useClinicalRegisterContext } from "@/contexts/ClinicalRegisterContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { PostAppointment, PostSlot } from "@/services/fhir";
import { PostWhereby } from "@/services/whereby/PostWhereby";
import { useFormatter } from "@/utils/useFormatter";
import type { ProntoAtendimentoConfig } from "./types";
import type { ProntoSelectedPatient } from "./ProntoAtendimentoPatientStep";
import type { ProntoActionAppointment } from "./mapProntoAppointmentToQueueItem";

const SLOT_DURATION_MINUTES = 30;
export const PRONTO_ATENDIMENTO_TYPE_SERVICE = "prontoatendimento";

/** Remoto e chat: mesma regra da agenda (ConfirmAppointmentDialog / isVirtual). */
function needsWherebyRoom(mode: ProntoAtendimentoConfig["mode"]): boolean {
  return mode === "remoto" || mode === "chat";
}

interface CreateParams {
  patient: ProntoSelectedPatient;
  config: ProntoAtendimentoConfig;
  value: number;
}

export function buildProntoActionAppointment(
  appointment: fhir5.Appointment,
  extras?: { patientName?: string; patientPhone?: string },
): ProntoActionAppointment {
  return {
    id: appointment.id,
    status: appointment.status,
    startTime: appointment.start,
    endTime: appointment.end,
    patientName: extras?.patientName,
    patientPhone: extras?.patientPhone,
    originalData: appointment,
  };
}

export function useProntoAtendimentoAppointment() {
  const { user } = useSessionContext();
  const { verifyEncounter } = useClinicalRegisterContext();
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);

  /** Só cria Slot + Appointment (booked). Não inicia Encounter. */
  const createAppointment = async ({
    patient,
    config,
    value,
  }: CreateParams): Promise<{
    appointment: fhir5.Appointment;
    actionAppointment: ProntoActionAppointment;
  } | null> => {
    if (!config.id || !config.locationId) {
      toast.error("Agenda de Pronto Atendimento incompleta (sem Location).");
      return null;
    }

    setLoading(true);
    try {
      const start = dayjs();
      const end = start.add(SLOT_DURATION_MINUTES, "minute");

      const practitionerDisplay = useFormatter.formatNameDisplay(
        user.name || "",
      );
      const practitionerRef = `Practitioner/${oids.cpf}-${user.username}`;
      const locationRef = `Location/${config.locationId}`;
      const locationDisplay = config.locationDisplay || config.locationId;

      const createdSlot = await PostSlot({
        type: PRONTO_ATENDIMENTO_TYPE_SERVICE,
        scheduleId: config.id,
        start: start.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
        end: end.format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
      });

      if (!createdSlot?.id) {
        throw new Error("Slot não criado");
      }

      const healthCareServiceActor = config.healthcareServiceId
        ? {
            reference: `HealthcareService/${config.healthcareServiceId}`,
            display:
              config.healthcareServiceDisplay ||
              config.specialty ||
              config.healthcareServiceId,
          }
        : undefined;

      // Sala Whereby (remoto/chat) — espelho de useAppointmentSubmission.setupVirtualService
      let virtualService:
        | Array<{ addressUrl: string; sessionKey: string }>
        | undefined;
      if (needsWherebyRoom(config.mode)) {
        try {
          const response = await PostWhereby({
            endDate: dayjs(createdSlot.end || end).format(
              "YYYY-MM-DDTHH:mm:ss-03:00",
            ),
            roomNamePrefix: `${endpoints?.AppName}-whereby`,
            roomNamePattern: "human-short",
          });
          if (response?.roomUrl) {
            virtualService = [
              {
                addressUrl: response.roomUrl,
                sessionKey: response.meetingId || "",
              },
            ];
          }
        } catch (wherebyError) {
          // Agenda engole o erro e segue sem sala — mesmo padrão
          console.error(
            "useProntoAtendimentoAppointment.whereby",
            wherebyError,
          );
        }
      }

      const appointment = await PostAppointment({
        typeService: PRONTO_ATENDIMENTO_TYPE_SERVICE,
        cpf: patient.cpf,
        cpfResponsible: patient.responsibleCpf,
        appointmentIsFree: true,
        appointmentStatus: "booked",
        serviceCost: value > 0 ? String(value) : "",
        schedule: config.id,
        slotInfo: {
          id: createdSlot.id,
          start: createdSlot.start,
          end: createdSlot.end,
        },
        patientActor: {
          reference: patient.reference,
          display: patient.display,
        },
        locationActor: {
          reference: locationRef,
          display: locationDisplay,
        },
        professionalActor: {
          reference: practitionerRef,
          display: practitionerDisplay,
        },
        ...(healthCareServiceActor && { healthCareServiceActor }),
        ...(virtualService && { virtualService }),
      });

      if (!appointment?.id) {
        throw new Error("Appointment não criado");
      }

      // FHIR exige status em cada participant
      const baseParticipants: fhir5.AppointmentParticipant[] = [
        {
          status: "accepted",
          actor: {
            reference: patient.reference,
            display: patient.display,
          },
        },
        {
          status: "accepted",
          actor: {
            reference: locationRef,
            display: locationDisplay,
          },
        },
      ];
      if (healthCareServiceActor) {
        baseParticipants.push({
          status: "accepted",
          actor: healthCareServiceActor,
        });
      }
      baseParticipants.push({
        status: "accepted",
        actor: {
          reference: practitionerRef,
          display: practitionerDisplay,
        },
      });

      // Preserva virtualService do POST ou do payload Whereby (lembrete / cancel)
      const resolvedVirtualService =
        appointment.virtualService ??
        (virtualService
          ? (virtualService as fhir5.Appointment["virtualService"])
          : undefined);

      const normalized: fhir5.Appointment = {
        ...appointment,
        resourceType: "Appointment",
        id: appointment.id,
        status: appointment.status || "booked",
        start: appointment.start || createdSlot.start,
        end: appointment.end || createdSlot.end,
        appointmentType: appointment.appointmentType || {
          coding: [
            {
              system: "http://terminology.hl7.org/CodeSystem/v2-0276",
              code: PRONTO_ATENDIMENTO_TYPE_SERVICE,
            },
          ],
          text: "Pronto Atendimento",
        },
        participant: appointment.participant?.length
          ? appointment.participant
          : baseParticipants,
        slot: appointment.slot || [{ reference: `Slot/${createdSlot.id}` }],
        ...(resolvedVirtualService && {
          virtualService: resolvedVirtualService,
        }),
      };

      toast.success("Atendimento confirmado.");

      return {
        appointment: normalized,
        actionAppointment: buildProntoActionAppointment(normalized, {
          patientName: patient.display,
          patientPhone: patient.phone,
        }),
      };
    } catch (error) {
      console.error("useProntoAtendimentoAppointment.create", error);
      toast.error("Não foi possível confirmar o atendimento.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  /** Inicia Encounter a partir do Appointment já criado. */
  const startEncounter = async (appointment: fhir5.Appointment) => {
    if (!appointment?.id) {
      toast.error("Consulta inválida para iniciar.");
      return false;
    }
    setStarting(true);
    try {
      await verifyEncounter(appointment);
      return true;
    } catch (error) {
      console.error("useProntoAtendimentoAppointment.start", error);
      toast.error("Não foi possível iniciar a consulta.");
      return false;
    } finally {
      setStarting(false);
    }
  };

  return {
    createAppointment,
    startEncounter,
    loading,
    starting,
  };
}
